import { useParams, Link } from "react-router-dom";
import { CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";
import { getService, enabledServices, images, processSteps, site } from "@/config/site";
import { SEO, serviceLd, breadcrumbLd, faqLd } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { CTA } from "@/components/CTA";
import { FAQAccordion } from "@/components/FAQAccordion";
import NotFound from "@/pages/NotFound";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);
  if (!service) return <NotFound />;

  const related = enabledServices.filter((s) => s.slug !== slug).slice(0, 3);
  const img = images[service.image];

  return (
    <>
      <SEO
        title={`${service.name} in Delta, BC | North Ember Painting`}
        description={`${service.short} Serving Delta, Ladner, Tsawwassen and Surrey. Fully insured, ${site.yearsExperience} years of experience. Request a free written estimate.`}
        path={`/services/${service.slug}`}
        image={img.url}
        jsonLd={[
          serviceLd(service),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Painting Services", path: "/services" },
            { name: service.name, path: `/services/${service.slug}` },
          ]),
          service.faqs.length ? faqLd(service.faqs) : null,
        ]}
      />

      <section className="relative overflow-hidden bg-ink" data-testid="service-hero">
        <img src={img.url} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/40" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
          <Reveal>
            <nav aria-label="Breadcrumb" className="text-xs font-medium uppercase tracking-widest text-slate-400">
              <Link to="/" className="hover:text-ember">Home</Link> / <Link to="/services" className="hover:text-ember">Services</Link> /{" "}
              <span className="text-ember">{service.name}</span>
            </nav>
            <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl">
              {service.name} in Delta, BC
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">{service.short}</p>
            <div className="mt-8">
              <Link
                to="/estimate"
                data-testid="service-estimate-btn"
                className="inline-flex min-h-[48px] items-center gap-2 rounded-full bg-ember px-7 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-900/40 transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-ember-dark"
              >
                Get a Free Estimate <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-3">
            <div className="space-y-14 lg:col-span-2">
              <Reveal>
                <div className="space-y-5 text-base leading-relaxed text-slate-600">
                  {service.intro.map((p, i) => <p key={i}>{p}</p>)}
                </div>
                <p className="mt-4 text-xs italic text-slate-400">Photography on this page is concept imagery, shown to illustrate the style of work.</p>
              </Reveal>

              <Reveal>
                <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">What's included</h2>
                <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {service.included.map((item) => (
                    <li key={item} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-sm leading-relaxed text-slate-700 shadow-sm">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-ember" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Reveal>

              <Reveal>
                <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Surface preparation</h2>
                <p className="mt-3 text-sm text-slate-500">Preparation is where lasting finishes come from. On every {service.name.toLowerCase()} project we:</p>
                <ul className="mt-5 space-y-3">
                  {service.preparation.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-slate-700">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-ember" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Reveal>

              <Reveal>
                <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">How your project unfolds</h2>
                <ol className="mt-6 space-y-0">
                  {processSteps.map((step, i) => (
                    <li key={step.title} className="relative flex gap-5 pb-8 last:pb-0">
                      {i < processSteps.length - 1 && (
                        <span className="absolute left-[15px] top-8 h-full w-px bg-slate-200" aria-hidden="true" />
                      )}
                      <span className="z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ember/10 font-display text-sm font-bold text-ember">
                        {i + 1}
                      </span>
                      <div>
                        <h3 className="font-display text-base font-semibold text-slate-900">{step.title}</h3>
                        <p className="mt-1 text-sm leading-relaxed text-slate-600">{step.text}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </Reveal>

              <Reveal>
                <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Worth knowing before you book</h2>
                <ul className="mt-6 space-y-3">
                  {service.considerations.map((item) => (
                    <li key={item} className="flex items-start gap-3 rounded-xl border border-amber-200/70 bg-amber-50/60 p-4 text-sm leading-relaxed text-slate-700">
                      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Reveal>

              {service.faqs.length > 0 && (
                <Reveal>
                  <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                    {service.name} FAQs
                  </h2>
                  <div className="mt-4">
                    <FAQAccordion faqs={service.faqs} idPrefix={`svc-${service.slug}`} />
                  </div>
                </Reveal>
              )}
            </div>

            <aside className="lg:sticky lg:top-24 lg:self-start">
              <Reveal>
                <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-lg">
                  <h2 className="font-display text-xl font-semibold text-slate-900">Get a free written estimate</h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    Tell us about your {service.name.toLowerCase()} project and we'll follow up to discuss it.{" "}
                    {site.hoursShort}.
                  </p>
                  <Link
                    to="/estimate"
                    data-testid="service-sidebar-estimate"
                    className="mt-5 flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-ember px-6 py-3 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-ember-dark"
                  >
                    Request an Estimate <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                  <a
                    href={site.phoneHref}
                    data-testid="service-sidebar-phone"
                    className="mt-3 flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-800 transition-colors duration-200 hover:border-ember hover:text-ember-dark"
                  >
                    Call {site.phone}
                  </a>
                </div>
              </Reveal>
              <Reveal delay={100}>
                <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-7">
                  <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Related services</h2>
                  <ul className="mt-4 space-y-3">
                    {related.map((r) => (
                      <li key={r.slug}>
                        <Link
                          to={`/services/${r.slug}`}
                          data-testid={`related-service-${r.slug}`}
                          className="group flex items-center justify-between text-sm font-medium text-slate-700 transition-colors duration-200 hover:text-ember-dark"
                        >
                          {r.name}
                          <ArrowRight className="h-4 w-4 text-slate-300 transition-all duration-300 group-hover:translate-x-1 group-hover:text-ember" aria-hidden="true" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </aside>
          </div>
        </div>
      </section>

      <CTA title={`Planning a ${service.name.toLowerCase()} project?`} />
    </>
  );
}
